import type { NavigationMenuItem } from '@nuxt/ui'
import { Role } from '~/model/enums/Role'
import { useDashboard } from '~/features/useDashboard'
import { useUserStore } from '~/store/useUserStore'
import { useOrder } from '~/features/orders/useOrder'
import { LazyConfirmationNew } from '#components'

// Define a type for menu items with roles
type MenuItemWithRoles = {
    label: string;
    to?: string;
    icon: string;
    roles: Role[];
    onSelect?: () => void;
    children?: MenuItemWithRoles[];
}

export const useRolesAccesses = () => {
    const { isSidebarOpened } = useDashboard()
    const { role } = useUserStore()
    const { orderSetEmergency } = useOrder()

    const overlay = useOverlay()

    const emergencyModal = overlay.create(LazyConfirmationNew, {
        props: {
            isQuestionRequired: true,
            questionText: 'Сообщить о непредвиденной ситуации',
            questionInputText: 'Что произошло?',
            agreeText: 'Отправить',
            declineText: 'Отмена',
            callBack: async(text: string) => {
                await orderSetEmergency(text)
            },
        },
    })

    const menuItemsNamesByRole: Record<string, Partial<Record<Role, string>>> = {
        newOrder: {
            [Role.MANAGER]: 'Все заказы',
        },
    }

    // Define all menu items with their roles
    const allMenuItems: MenuItemWithRoles[] = [
        {
            label: 'Дашборд',
            to: '/',
            icon: 'i-lucide-layout-dashboard',
            roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN],
            onSelect: () => {
                isSidebarOpened.value = false
            },
        },
        {
            label: 'Новый заказ',
            to: '/orders/create-new',
            icon: 'i-lucide-plus-circle',
            roles: [Role.MANAGER, Role.LEAD_OF_LOGISTICIAN, Role.PARTNER, Role.DROP_SHIPPER, Role.COLD_SALES_MANAGER],
            onSelect: () => {
                isSidebarOpened.value = false
            },
        },
        {
            label: menuItemsNamesByRole.newOrder?.[unref(role)] ?? 'Заказы',
            icon: 'i-lucide-shopping-cart',
            roles: [
                Role.SUPERADMIN,
                Role.DEVELOPER,
                Role.ADMIN,
                Role.HEAD_OF_SALES,
                Role.MANAGER,
                Role.LOGISTICIAN,
                Role.LEAD_OF_LOGISTICIAN,
                Role.PARTNER,
                Role.DROP_SHIPPER,
                Role.WAREHOUSE_WORKER,
                Role.COLD_SALES_MANAGER,
                Role.RECLAMATION_MANAGER,
            ],
            onSelect: () => {
                isSidebarOpened.value = false
            },
            children: [
                {
                    label: 'Все заказы',
                    to: '/orders',
                    icon: 'i-lucide-list',
                    roles: [
                        Role.SUPERADMIN,
                        Role.DEVELOPER,
                        Role.ADMIN,
                        Role.HEAD_OF_SALES,
                        Role.MANAGER,
                        Role.LOGISTICIAN,
                        Role.LEAD_OF_LOGISTICIAN,
                        Role.PARTNER,
                        Role.DROP_SHIPPER,
                        Role.WAREHOUSE_WORKER,
                        Role.COLD_SALES_MANAGER,
                        Role.RECLAMATION_MANAGER,
                    ],
                    onSelect: () => {
                        isSidebarOpened.value = false
                    },
                },
                {
                    label: 'Все заказы (обновленные)',
                    to: '/orders/new',
                    icon: 'i-lucide-list',
                    roles: [
                        Role.DEVELOPER,
                    ],
                    onSelect: () => {
                        isSidebarOpened.value = false
                    },
                },
                {
                    label: 'Новый заказ',
                    to: '/orders/create-new',
                    icon: 'i-lucide-plus-circle',
                    roles: [
                        Role.SUPERADMIN,
                        Role.DEVELOPER,
                        Role.ADMIN,
                    ],
                    onSelect: () => {
                        isSidebarOpened.value = false
                    },
                },
            ],
        },
        {
            label: 'Мои заказы',
            to: '/orders/my',
            icon: 'i-lucide-shopping-cart',
            roles: [
                Role.MANAGER,
            ],
            onSelect: () => {
                isSidebarOpened.value = false
            },
        },
        {
            label: 'Текущие заказы',
            to: '/orders/current',
            icon: 'i-lucide-shopping-cart',
            roles: [Role.DRIVER],
            onSelect: () => {
                isSidebarOpened.value = false
            },
        },
        {
            label: 'Завершенные заказы',
            to: '/orders/finished',
            icon: 'i-lucide-check-square',
            roles: [Role.DRIVER],
            onSelect: () => {
                isSidebarOpened.value = false
            },
        },
        {
            label: 'Заказы с заменой',
            to: '/replacement-orders',
            icon: 'i-lucide-replace',
            roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN, Role.MANAGER],
            onSelect: () => {
                isSidebarOpened.value = false
            },
        },
        {
            label: 'Заказы с возвратом на склад',
            to: '/return-to-warehouse-orders',
            icon: 'i-lucide-undo-2',
            roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN, Role.WAREHOUSE_WORKER, Role.LOGISTICIAN, Role.LEAD_OF_LOGISTICIAN],
            onSelect: () => {
                isSidebarOpened.value = false
            },
        },
        {
            label: 'Каталог',
            icon: 'i-lucide-package',
            roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN],
            onSelect: () => {
                isSidebarOpened.value = false
            },
            children: [
                {
                    label: 'Все товары',
                    to: '/products',
                    icon: 'i-lucide-list',
                    roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN],
                    onSelect: () => {
                        isSidebarOpened.value = false
                    },
                },
                {
                    label: 'Новый товар',
                    to: '/products/create',
                    icon: 'i-lucide-plus-circle',
                    roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN],
                    onSelect: () => {
                        isSidebarOpened.value = false
                    },
                },
                {
                    label: 'Основания кроватей',
                    to: '/products/bed-bases',
                    icon: 'i-lucide-bed',
                    roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN],
                    onSelect: () => {
                        isSidebarOpened.value = false
                    },
                },
                {
                    label: 'Цвета',
                    to: '/products/colors',
                    icon: 'i-lucide-palette',
                    roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN],
                    onSelect: () => {
                        isSidebarOpened.value = false
                    },
                },
                {
                    label: 'Типы цветов',
                    to: '/products/color-types',
                    icon: 'i-lucide-swatch-book',
                    roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN],
                    onSelect: () => {
                        isSidebarOpened.value = false
                    },
                },
            ],
        },
        {
            label: 'Пользователи',
            to: '/users',
            icon: 'i-lucide-users',
            roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN],
            onSelect: () => {
                isSidebarOpened.value = false
            },
        },
        {
            label: 'Подтверждения',
            to: '/approvals',
            icon: 'i-lucide-check-circle',
            roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN, Role.HEAD_OF_SALES, Role.LOGISTICIAN, Role.LEAD_OF_LOGISTICIAN],
            onSelect: () => {
                isSidebarOpened.value = false
            },
        },
        {
            label: 'Склад',
            icon: 'i-lucide-warehouse',
            roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN, Role.WAREHOUSE_WORKER, Role.RECLAMATION_MANAGER, Role.LOGISTICIAN, Role.LEAD_OF_LOGISTICIAN],
            onSelect: () => {
                isSidebarOpened.value = false
            },
            children: [
                {
                    label: 'Заказы на склад',
                    to: '/warehouse/order/list',
                    icon: 'i-lucide-list',
                    roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN, Role.WAREHOUSE_WORKER, Role.RECLAMATION_MANAGER],
                    onSelect: () => {
                        isSidebarOpened.value = false
                    },
                },
                {
                    label: 'Позиции склада',
                    to: '/warehouse/positions',
                    icon: 'i-lucide-box',
                    roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN],
                    onSelect: () => {
                        isSidebarOpened.value = false
                    },
                },
                {
                    label: 'Остатки',
                    to: '/warehouse/stocks',
                    icon: 'i-lucide-layers',
                    roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN, Role.WAREHOUSE_WORKER, Role.LOGISTICIAN, Role.LEAD_OF_LOGISTICIAN],
                    onSelect: () => {
                        isSidebarOpened.value = false
                    },
                },
                {
                    label: 'Номенклатуры',
                    to: '/warehouse/nomenclatures',
                    icon: 'i-lucide-barcode',
                    roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN],
                    onSelect: () => {
                        isSidebarOpened.value = false
                    },
                },
                {
                    label: 'Состав товаров',
                    to: '/warehouse/composition',
                    icon: 'i-lucide-network',
                    roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN],
                    onSelect: () => {
                        isSidebarOpened.value = false
                    },
                },
            ],
        },
        {
            label: 'Назначение водителей',
            to: '/select-drivers',
            icon: 'i-lucide-truck',
            roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN, Role.LOGISTICIAN, Role.LEAD_OF_LOGISTICIAN],
            onSelect: () => {
                isSidebarOpened.value = false
            },
        },
        {
            label: 'Ожидает обзвона логистом',
            to: '/logistician-call',
            icon: 'i-lucide-phone-call',
            roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN, Role.LOGISTICIAN, Role.LEAD_OF_LOGISTICIAN],
            onSelect: () => {
                isSidebarOpened.value = false
            },
        },
        {
            label: 'Промокоды',
            to: '/promocodes',
            icon: 'i-lucide-ticket',
            roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN, Role.COLD_SALES_MANAGER],
            onSelect: () => {
                isSidebarOpened.value = false
            },
        },
        {
            label: 'Менеджеры',
            to: '/managers',
            icon: 'i-lucide-user-cog',
            roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN, Role.MANAGER],
            onSelect: () => {
                isSidebarOpened.value = false
            },
        },
        {
            label: 'Водители',
            to: '/drivers',
            icon: 'i-lucide-car',
            roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN, Role.LEAD_OF_LOGISTICIAN, Role.LOGISTICIAN],
            onSelect: () => {
                isSidebarOpened.value = false
            },
        },
        {
            label: 'Поставщики',
            to: '/suppliers',
            icon: 'i-lucide-store',
            roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN],
            onSelect: () => {
                isSidebarOpened.value = false
            },
        },
        {
            label: 'Источники',
            to: '/sources',
            icon: 'i-lucide-link',
            roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN],
            onSelect: () => {
                isSidebarOpened.value = false
            },
        },
        {
            label: 'Сборки',
            to: '/assemblies',
            icon: 'i-lucide-hammer',
            roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN, Role.COLLECTOR_OF_FURNITURE, Role.LEAD_OF_FURNITURE_COLLECTORS],
            onSelect: () => {
                isSidebarOpened.value = false
            },
        },
        {
            label: 'Рекламации',
            to: '/reclamations',
            icon: 'i-lucide-alert-circle',
            roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN, Role.LEAD_OF_LOGISTICIAN, Role.LOGISTICIAN, Role.HEAD_OF_SALES, Role.WAREHOUSE_WORKER, Role.DRIVER, Role.RECLAMATION_MANAGER],
            onSelect: () => {
                isSidebarOpened.value = false
            },
        },
        {
            label: 'Возвраты',
            to: '/returns',
            icon: 'i-lucide-rotate-ccw',
            roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN, Role.LEAD_OF_LOGISTICIAN, Role.LOGISTICIAN, Role.HEAD_OF_SALES, Role.MANAGER, Role.CALL_CENTER_OPERATOR, Role.WAREHOUSE_WORKER, Role.DRIVER],
            onSelect: () => {
                isSidebarOpened.value = false
            },
        },
        {
            label: 'Довозы/замены',
            to: '/supplies',
            icon: 'i-lucide-rotate-ccw',
            roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN, Role.LEAD_OF_LOGISTICIAN, Role.LOGISTICIAN, Role.HEAD_OF_SALES, Role.MANAGER, Role.CALL_CENTER_OPERATOR, Role.WAREHOUSE_WORKER, Role.DRIVER],
            onSelect: () => {
                isSidebarOpened.value = false
            },
        },
        {
            label: 'Магазин',
            icon: 'i-lucide-armchair',
            roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN, Role.RECLAMATION_MANAGER],
            onSelect: () => {
                isSidebarOpened.value = false
            },
            children: [
                {
                    label: 'Баннеры',
                    to: '/shop/banners',
                    icon: 'i-lucide-image',
                    roles: [Role.DEVELOPER],
                    onSelect: () => {
                        isSidebarOpened.value = false
                    },
                },
                {
                    label: 'Категории',
                    to: '/shop/categories',
                    icon: 'i-lucide-list',
                    roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN],
                    onSelect: () => {
                        isSidebarOpened.value = false
                    },
                },
                {
                    label: 'Отзывы',
                    to: '/shop/reviews',
                    icon: 'i-lucide-message-square-more',
                    roles: [Role.DEVELOPER, Role.ADMIN, Role.SUPERADMIN, Role.RECLAMATION_MANAGER],
                    onSelect: () => {
                        isSidebarOpened.value = false
                    },
                },
                {
                    label: 'Кэш',
                    to: '/shop/cache',
                    icon: 'octicon:cache-16',
                    roles: [Role.DEVELOPER, Role.ADMIN, Role.SUPERADMIN],
                    onSelect: () => {
                        isSidebarOpened.value = false
                    },
                },
            ],
        },
        {
            label: 'Рейсы',
            to: '/trips',
            icon: 'i-lucide-route',
            roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN, Role.LEAD_OF_LOGISTICIAN, Role.HEAD_OF_SALES, Role.LOGISTICIAN],
            onSelect: () => {
                isSidebarOpened.value = false
            },
        },
        {
            label: 'Текущие Рейсы',
            to: '/trips/current',
            icon: 'i-lucide-route',
            roles: [Role.DRIVER],
            onSelect: () => {
                isSidebarOpened.value = false
            },
        },
        {
            label: 'Разработчикам',
            icon: 'iconoir:developer',
            roles: [Role.DEVELOPER],
            children: [
                {
                    label: 'Статусы заказов',
                    icon: 'lets-icons:status-list',
                    to: '/developer/order-statuses',
                    roles: [Role.DEVELOPER],
                    onSelect: () => {
                        isSidebarOpened.value = false
                    },
                },
            ],
        },
    ]

    // Function to filter menu items by role
    const getMenuItemsByRole = (role: Role) => {
        if (role === Role.UNAUTHORIZED) {
            return []
        }

        // Filter top-level items by role
        return allMenuItems
            .filter(item => item.roles.includes(role))
            .map((item) => {
                // Create a new object without the roles property
                const { roles, ...menuItem } = item

                // If the item has children, filter them by role as well
                if (item.children) {
                    const filteredChildren = item.children
                        .filter(child => child.roles.includes(role))
                        .map((child) => {
                            const { roles: childRoles, ...childMenuItem } = child
                            return childMenuItem
                        })

                    return { ...menuItem, children: filteredChildren }
                }

                return menuItem as NavigationMenuItem
            })
    }

    const menuItems = computed(() => getMenuItemsByRole(unref(role)) ?? [])

    const bottomItems = [
        ...(unref(role) === Role.DRIVER
            ? [{
                label: 'Непредвиденная ситуация',
                icon: 'i-lucide-message-circle-warning',
                roles: [Role.SUPERADMIN, Role.DEVELOPER, Role.ADMIN],
                ui: {
                    link: 'text-red-500',
                    linkLeadingIcon: 'text-red-500',
                },
                onSelect: () => {
                    emergencyModal.open()
                },
            }] as NavigationMenuItem[]
            : []),
    ] as NavigationMenuItem[]

    return { menuItems, bottomItems }
}
